/**
 * ArtsAwardDetail — /arts/awards/:slug
 * One award, all editions grouped by year, each recipient linked
 * to their arts_entities profile when known.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Award, ExternalLink, PenSquare, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ArtsAwardDetail() {
  const { slug } = useParams();
  const { user }  = useAuth();
  const [award, setAward] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    year: "", category: "", recipient_name: "", recipient_slug: "", work: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/arts/awards/${encodeURIComponent(slug)}`);
      setAward(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Not found");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const editionsByYear = useMemo(() => {
    const eds = award?.editions || [];
    const byYear = new Map();
    for (const e of eds) {
      const y = e.year || 0;
      if (!byYear.has(y)) byYear.set(y, []);
      byYear.get(y).push(e);
    }
    return Array.from(byYear.entries()).sort((a, b) => b[0] - a[0]);
  }, [award]);

  const addEdition = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to add an edition"); return; }
    if (!form.year || !form.recipient_name) { toast.error("Year and recipient are required"); return; }
    setBusy(true);
    try {
      await http.post(`/arts/awards/${encodeURIComponent(slug)}/editions`, {
        year:           Number(form.year),
        category:       form.category || undefined,
        recipient_name: form.recipient_name,
        recipient_slug: form.recipient_slug || undefined,
        work:           form.work || undefined,
      });
      toast.success("Edition added");
      setForm({ year: "", category: "", recipient_name: "", recipient_slug: "", work: "" });
      setAdding(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add edition");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "rounded border px-2 py-1.5 text-sm bg-transparent";
  const border   = { borderColor: "var(--wofy-border)" };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="arts-award-detail">
      <Link to="/arts/awards" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> All awards
      </Link>

      {loading && (
        <div className="wofy-card !p-6 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      )}
      {!loading && error && (
        <div className="wofy-card !p-6" style={{ background: "#FEF3C7" }}>{error}</div>
      )}
      {!loading && award && (
        <>
          <header className="mb-6">
            <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
              {award.kind}{award.country ? ` · ${award.country}` : ""}
            </div>
            <h1 className="font-display text-4xl inline-flex items-center gap-2">
              <Award className="w-7 h-7" /> {award.name}
            </h1>
            {award.description && <p className="opacity-90 mt-2">{award.description}</p>}
            <dl className="text-sm mt-3 space-y-1 opacity-80">
              {award.presenting_body && <div><span className="opacity-70">Presented by:</span> {award.presenting_body}</div>}
              {award.founded_year && <div><span className="opacity-70">Founded:</span> {award.founded_year}</div>}
              {award.website && (
                <div>
                  <a href={award.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--wofy-primary)" }}>
                    <ExternalLink className="w-3 h-3" /> Official website
                  </a>
                </div>
              )}
            </dl>
          </header>

          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Editions & recipients</h2>
            {user && (
              <button
                type="button"
                onClick={() => setAdding((a) => !a)}
                className="wofy-btn-ghost text-xs"
                data-testid="arts-award-add-edition"
              >
                <PenSquare className="w-3 h-3" /> {adding ? "Cancel" : "Add edition"}
              </button>
            )}
          </div>

          {adding && (
            <form onSubmit={addEdition} className="wofy-card !p-4 mb-4 space-y-2" data-testid="arts-award-edition-form">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <input type="number" placeholder="Year" value={form.year}
                       onChange={(e) => setForm(f => ({ ...f, year: e.target.value }))}
                       className={inputCls} style={border} required data-testid="edition-year" />
                <input placeholder="Category" value={form.category}
                       onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                       className={inputCls} style={border} data-testid="edition-category" />
                <input placeholder="Recipient name" value={form.recipient_name}
                       onChange={(e) => setForm(f => ({ ...f, recipient_name: e.target.value }))}
                       className={`${inputCls} md:col-span-2`} style={border} required data-testid="edition-recipient" />
                <input placeholder="Work / film / album" value={form.work}
                       onChange={(e) => setForm(f => ({ ...f, work: e.target.value }))}
                       className={inputCls} style={border} data-testid="edition-work" />
              </div>
              <div className="flex items-center justify-between gap-2">
                <input placeholder="Optional arts-profile slug (e.g. ravi-shankar)"
                       value={form.recipient_slug}
                       onChange={(e) => setForm(f => ({ ...f, recipient_slug: e.target.value }))}
                       className={`${inputCls} flex-1`} style={border} data-testid="edition-slug" />
                <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="edition-submit">
                  {busy ? "Saving…" : "Add"}
                </button>
              </div>
            </form>
          )}

          {editionsByYear.length === 0 && (
            <div className="wofy-card !p-6 opacity-80 text-center">
              No editions on record yet. Be the first to add one.
            </div>
          )}

          <ol className="space-y-6" data-testid="arts-award-editions">
            {editionsByYear.map(([year, eds]) => (
              <li key={year}>
                <div className="text-xs font-mono uppercase tracking-wider opacity-60 mb-1">{year || "Year unknown"}</div>
                <ul className="space-y-1 border-l-2 pl-4" style={{ borderColor: "var(--wofy-border)" }}>
                  {eds.map((e) => (
                    <li key={e.id} className="text-sm">
                      {e.recipient_slug ? (
                        <Link to={`/arts/artists/${e.recipient_slug}`} className="font-medium hover:underline"
                              style={{ color: "var(--wofy-primary)" }}
                              data-testid={`arts-award-recipient-${e.recipient_slug}`}>
                          {e.recipient_name}
                        </Link>
                      ) : (
                        <span className="font-medium">{e.recipient_name}</span>
                      )}
                      {e.category && <span className="opacity-70"> · {e.category}</span>}
                      {e.work && <span className="opacity-70"> — {e.work}</span>}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
